import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, ChevronDown, Settings } from "lucide-react";
import { cn } from "@/modules/shared/lib/utils";
import { Button } from "@/modules/shared/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/modules/shared/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/modules/shared/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modules/shared/components/ui/collapsible";
import { useAuth } from "@/modules/shared/hooks/useAuth";
import { usePortalAuth } from "@/modules/portal/hooks/usePortalAuth";
import { useUserPermissions } from "@/modules/shared/hooks/usePermissions";
import { useQuery } from "@tanstack/react-query";
import { db } from "@/modules/shared/lib/db-client";
import { useBranding } from "@/modules/shared/hooks/useBranding";
import { usePlatformAdmin } from "@/modules/shared/hooks/usePlatformAdmin";
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCheck,
  MessageSquare,
  ClipboardCheck,
  Briefcase,
  Target,
  Activity,
  Receipt,
  Calculator,
  Heart,
  Trophy,
  FileCheck,
  Zap,
  FileBarChart,
  DollarSign,
  ClipboardList,
  FolderKanban,
  Globe,
  Clock,
  UsersRound,
  BarChart3,
  TrendingUp,
  Shield,
} from "lucide-react";

interface MenuItem {
  name: string;
  href: string;
  icon: React.ElementType;
  featureKey?: string;
  exact?: boolean;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    title: "Gestão",
    items: [
      { name: "Clientes", href: "/clientes", icon: Building2, featureKey: "clientes" },
      { name: "Time", href: "/profissionais", icon: Users, featureKey: "profissionais" },
    ],
  },
  {
    title: "Campeonato",
    items: [
      { name: "OpenCode", href: "/opencode/admin", icon: Trophy, featureKey: "opencode" },
    ],
  },
  {
    title: "Recrutamento",
    items: [
      { name: "Vagas", href: "/vagas", icon: Briefcase, featureKey: "vagas" },
      { name: "Página de Carreiras", href: "/carreiras/editor", icon: Globe, featureKey: "vagas" },
      { name: "Avaliações", href: "/recrutamento", icon: ClipboardList, featureKey: "recrutamento" },
    ],
  },
  {
    title: "Operações",
    items: [
      { name: "Projetos", href: "/projetos", icon: FolderKanban },
      { name: "Squads", href: "/squads", icon: UsersRound, featureKey: "squad" },
    ],
  },
  {
    title: "Acompanhamento",
    items: [
      { name: "Performance", href: "/checkins", icon: MessageSquare, featureKey: "checkins" },
      { name: "Planos de Ação", href: "/planos-de-acao", icon: Target, featureKey: "planos_acao" },
      { name: "PDI & Feedbacks", href: "/pdi", icon: FileCheck, featureKey: "pdi" },
      { name: "Timesheet", href: "/timesheet", icon: Clock, featureKey: "timesheet" },
      { name: "Cultura", href: "/cultura", icon: Heart, featureKey: "cultura" },
      { name: "Resumo Mensal", href: "/resumo-mensal", icon: ClipboardCheck, featureKey: "resumo_mensal" },
    ],
  },
  {
    title: "Resultados",
    items: [
      { name: "Painel Gestores", href: "/dashboard-engajamento", icon: Activity, featureKey: "painel_gestores" },
    ],
  },
  {
    title: "Comercial",
    items: [
      { name: "Cockpit Comercial", href: "/revenue", icon: BarChart3, featureKey: "minha_carteira" },
      { name: "Minha Carteira", href: "/minha-carteira", icon: Briefcase, featureKey: "minha_carteira" },
      { name: "Rate Card", href: "/rate-card", icon: Receipt, featureKey: "rate_card" },
      { name: "Calculadora", href: "/calculadora-comercial", icon: Calculator, featureKey: "calculadora" },
    ],
  },
  {
    title: "Financeiro",
    items: [
      { name: "Visão Financeira", href: "/financeiro", icon: DollarSign, featureKey: "financeiro" },
      { name: "Reembolsos", href: "/reembolsos", icon: Receipt, featureKey: "reembolsos" },
    ],
  },
];

const dashboardItem: MenuItem = {
  name: "Dashboard",
  href: "/dashboard",
  icon: LayoutDashboard,
  featureKey: "visao_geral",
  exact: true,
};

export function MobileMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isPortalUser, signOut: portalSignOut } = usePortalAuth();
  const permissions = useUserPermissions();
  const { isPlatformAdmin } = usePlatformAdmin();
  const { logoWhiteUrl, platformName } = useBranding();

  const { data: userProfile } = useQuery({
    queryKey: ["mobile-user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data: teamMember } = await db
        .from("professionals")
        .select("id, name, email, avatar_url, role")
        .eq("professional_type", "operacional")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (teamMember) return teamMember;
      
      return {
        id: user.id,
        name: user.email?.split("@")[0] || "Usuário",
        email: user.email || "",
        avatar_url: null,
        role: "admin",
      };
    },
    enabled: !!user?.id && !isPortalUser,
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const isActiveRoute = (href: string, exact?: boolean) => {
    if (exact) return location.pathname === href;
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  const handleLogout = async () => {
    setOpen(false);
    if (isPortalUser) {
      await portalSignOut();
      navigate("/portal/login");
    } else {
      await signOut();
      navigate("/auth");
    }
  };

  const handleNavClick = () => {
    setOpen(false);
  };

  const filterItems = (items: MenuItem[]) => {
    return items.filter(item => !item.featureKey || permissions[item.featureKey]);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden shrink-0">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Abrir menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0 bg-sidebar">
        <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
        <SheetDescription className="sr-only">
          Use este menu para navegar entre as páginas do sistema
        </SheetDescription>
        
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-sidebar-border/30">
            <img src={logoWhiteUrl} alt={platformName} className="h-8" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-3 space-y-4">
            {/* Dashboard */}
            {permissions.visao_geral && (
              <NavLink
                to={dashboardItem.href}
                onClick={handleNavClick}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                  isActiveRoute(dashboardItem.href, dashboardItem.exact)
                    ? "bg-accent text-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50"
                )}
              >
                <dashboardItem.icon className="h-5 w-5" />
                {dashboardItem.name}
              </NavLink>
            )}

            {/* Sections */}
            {menuSections.map((section) => {
              const filteredItems = filterItems(section.items);
              if (filteredItems.length === 0) return null;
              
              return (
                <div key={section.title}>
                  <p className="px-3 mb-2 text-xs uppercase tracking-wider text-sidebar-foreground/40 font-semibold">
                    {section.title}
                  </p>
                  <div className="space-y-1">
                    {filteredItems.map((item) => (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        onClick={handleNavClick}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                          isActiveRoute(item.href, item.exact)
                            ? "bg-accent text-accent-foreground"
                            : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50"
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Super Admin */}
            {isPlatformAdmin && (
              <div>
                <p className="px-3 mb-2 text-xs uppercase tracking-wider text-sidebar-foreground/40 font-semibold">
                  Super Admin
                </p>
                <div className="space-y-1">
                  <NavLink
                    to="/admin/painel"
                    onClick={handleNavClick}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                      isActiveRoute("/admin/painel")
                        ? "bg-accent text-accent-foreground"
                        : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50"
                    )}
                  >
                    <Shield className="h-5 w-5" />
                    Painel Admin
                  </NavLink>
                </div>
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="p-3 border-t border-sidebar-border/30 space-y-2">
            {/* User Profile */}
            {userProfile && (
              <div className="flex items-center gap-3 p-2 rounded-lg bg-sidebar-accent/30">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={userProfile.avatar_url || undefined} />
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs font-medium">
                    {getInitials(userProfile.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-sidebar-foreground truncate">{userProfile.name}</p>
                  <p className="text-xs text-sidebar-foreground/50 truncate">{userProfile.email}</p>
                </div>
              </div>
            )}

            {/* Settings */}
            {permissions.configuracoes && (
              <NavLink
                to="/configuracoes"
                onClick={handleNavClick}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                  isActiveRoute("/configuracoes")
                    ? "bg-accent text-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50"
                )}
              >
                <Settings className="h-5 w-5" />
                Configurações
              </NavLink>
            )}

            {/* Logout */}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start gap-3 px-3 py-2.5 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <LogOut className="h-5 w-5" />
              Sair
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
